import { el } from "../core/dom";
import { part } from "../appearance/parts";
import { OTP_GATE_COPY, OTP_GATE_TITLE_ID } from "../constants";
import type {
  OtpEntry,
  OtpGatePanelOptions,
  OtpGatePanelRefs,
  OtpGateView,
} from "../types";
import { applyOtpBackspace, applyOtpText, isOtpComplete } from "./otpGate";

const panelStyle = {
  margin: "8px 0 4px",
  padding: "12px",
  border: "1px solid #e5e7eb",
  borderRadius: "8px",
  background: "#f9fafb",
};

const titleStyle = {
  margin: "0",
  fontSize: "13px",
  fontWeight: "600",
  color: "#111827",
};

const descriptionStyle = {
  margin: "4px 0 10px",
  fontSize: "12px",
  lineHeight: "1.4",
  color: "#6b7280",
};

const digitsStyle = {
  display: "flex",
  gap: "8px",
  justifyContent: "space-between",
};

const digitStyle = {
  width: "100%",
  minWidth: "0",
  boxSizing: "border-box",
  padding: "10px 0",
  fontSize: "18px",
  fontWeight: "600",
  textAlign: "center",
  border: "1px solid #d1d5db",
  borderRadius: "8px",
  background: "#ffffff",
};

const errorStyle = {
  margin: "8px 0 0",
  fontSize: "12px",
  color: "#dc2626",
};

const buttonStyle = {
  width: "100%",
  marginTop: "10px",
  borderRadius: "8px",
  padding: "9px 20px",
  fontSize: "14px",
  lineHeight: "150%",
  border: "none",
  backgroundColor: "#4f87ff",
  color: "#ffffff",
  cursor: "pointer",
  boxSizing: "border-box",
};

export function renderOtpGatePanel(
  options: OtpGatePanelOptions
): OtpGatePanelRefs {
  let view: OtpGateView = options.view;
  const inputs: HTMLInputElement[] = [];

  const look = options.styles ?? {};
  const errorEl = el("p", {
    role: "alert",
    style: { ...errorStyle, ...look.error, display: "none" },
  });

  const confirmButton = el("button", {
    type: "button",
    style: { ...buttonStyle, ...look.button, ...options.buttonStyle },
    onClick: () => {
      if (view.busy || !isOtpComplete(view.digits)) return;
      options.onConfirm();
    },
  }) as HTMLButtonElement;

  const paint = () => {
    inputs.forEach((input, index) => {
      input.value = view.digits[index] ?? "";
      input.disabled = view.busy;
    });
    const blocked = view.busy || !isOtpComplete(view.digits);
    confirmButton.disabled = blocked;
    confirmButton.style.opacity = blocked ? "0.5" : "1";
    confirmButton.style.cursor = blocked ? "not-allowed" : "pointer";
    confirmButton.textContent = view.busy
      ? options.verifyingLabel ?? OTP_GATE_COPY.verifying
      : options.submitLabel;
    errorEl.textContent = view.error ?? "";
    errorEl.style.display = view.error ? "block" : "none";
  };

  const focusDigit = (index: number) => {
    inputs[index]?.focus();
  };

  const applyEntry = (entry: OtpEntry | null) => {
    if (!entry) {
      paint();
      return;
    }
    view = { ...view, digits: entry.digits };
    paint();
    options.onDigitsChange(entry.digits);
    if (entry.focus !== null) focusDigit(entry.focus);
  };

  const digitRow = el("div", { style: { ...digitsStyle, ...look.digits } });
  view.digits.forEach((_, index) => {
    const input = el("input", {
      id: `notice-otp-digit-${index}`,
      type: "text",
      inputmode: "numeric",
      autocomplete: index === 0 ? "one-time-code" : "off",
      "aria-label": `Digit ${index + 1}`,
      style: { ...digitStyle, ...look.digit },
      onFocus: () => input.select(),
      onInput: () => applyEntry(applyOtpText(view.digits, index, input.value)),
      onKeydown: (event: KeyboardEvent) => {
        if (event.key !== "Backspace") return;
        event.preventDefault();
        applyEntry(applyOtpBackspace(view.digits, index));
      },
      onPaste: (event: ClipboardEvent) => {
        event.preventDefault();
        const text = event.clipboardData?.getData("text") ?? "";
        applyEntry(applyOtpText(view.digits, index, text));
      },
    }) as HTMLInputElement;
    inputs.push(input);
    digitRow.appendChild(input);
  });

  const container = el(
    "div",
    {
      ...part("otp"),
      role: "group",
      "aria-labelledby": OTP_GATE_TITLE_ID,
      style: { ...panelStyle, ...look.panel },
    },
    el("h3", { id: OTP_GATE_TITLE_ID, style: { ...titleStyle, ...look.title } }, options.title),
    el("p", { style: { ...descriptionStyle, ...look.description } }, options.description),
    digitRow,
    errorEl,
    confirmButton
  );

  paint();

  return {
    container,
    render: (next: OtpGateView) => {
      view = next;
      paint();
    },
    focusDigit,
  };
}

export function lockSelections(container: HTMLElement): void {
  container.setAttribute("inert", "");
  container.setAttribute("aria-disabled", "true");
  container.style.pointerEvents = "none";
  container.style.opacity = "0.55";
  container
    .querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLButtonElement>(
      "input, select, button"
    )
    .forEach((control) => {
      control.disabled = true;
    });
  container
    .querySelectorAll<HTMLElement>('[role="button"]')
    .forEach((button) => {
      button.setAttribute("tabindex", "-1");
      button.setAttribute("aria-disabled", "true");
    });
}
